import { MutationTree } from 'vuex';
import {
  ICreateNodeRequest,
  IUpdateNodeRequest,
  IDeleteNodeRequest,
  IMoveNodesRequest,
} from '@/editor/api/services/types/requests';
import { ISection, IRow, ICell, INode } from '@/editor/types';
import { EEditorContentMutation } from '@/editor/editor-block/store/editor-content.constants';
import { IDealroomState } from '@/dealroom/store/dealroom.store';
import { IDocumentPage } from '@/types/document-page';
import { EditorHelper } from '@/editor/helpers/editor.helper';

export const mutations: MutationTree<IDealroomState> = {
  [EEditorContentMutation.ReplaceBlock](state: IDealroomState, newBlock: IDocumentPage) {
    state.document.pages = state.document.pages.map((block: IDocumentPage) => {
      if (
        !!block.editorBlock?.contentId &&
        !!newBlock.editorBlock?.contentId &&
        block.editorBlock.contentId === newBlock.editorBlock.contentId
      ) {
        return newBlock;
      }
      return block;
    });
  },

  [EEditorContentMutation.CreateNode](
    state: IDealroomState,
    {
      contentId,
      targetSectionId,
      targetRowId,
      targetCellId,
      targetNodeId,
      append,
      payload,
    }: ICreateNodeRequest
  ) {
    const node: INode = EditorHelper.getNewNode(payload);

    state.document.pages = EditorHelper.addNodes(state, {
      contentId,
      targetSectionId,
      targetRowId,
      targetCellId,
      targetNodeId,
      append,
      nodes: [node],
    });
  },

  [EEditorContentMutation.UpdateNode](
    state: IDealroomState,
    {
      contentId,
      targetSectionId,
      targetRowId,
      targetCellId,
      targetNodeId,
      payload,
    }: IUpdateNodeRequest
  ) {
    state.document.pages = state.document.pages.map((block: IDocumentPage) => {
      if (block.editorBlock?.contentId === contentId) {
        return {
          ...block,
          editorBlock: {
            ...block.editorBlock,
            content: {
              ...block.editorBlock.content,
              sections: block.editorBlock.content.sections.map((section: ISection) => {
                if (section.id === targetSectionId) {
                  return {
                    ...section,
                    rows: section.rows.map((row: IRow) => {
                      if (row.id === targetRowId) {
                        return {
                          ...row,
                          cells: row.cells.map((cell: ICell) => {
                            if (cell.id === targetCellId) {
                              return {
                                ...cell,
                                nodes: cell.nodes.map((node: INode) =>
                                  node.id === targetNodeId
                                    ? EditorHelper.mergeDeep(node, payload)
                                    : node
                                ),
                              };
                            }
                            return cell;
                          }),
                        };
                      }
                      return row;
                    }),
                  };
                }
                return section;
              }),
            },
          },
        };
      }
      return block;
    });
  },

  [EEditorContentMutation.MoveNodes](
    state: IDealroomState,
    {
      contentId,
      targetSectionId,
      targetRowId,
      targetCellId,
      targetNodeId,
      sourceSectionId,
      sourceRowId,
      sourceCellId,
      sourceNodeIds,
      append,
    }: IMoveNodesRequest
  ) {
    const nodes: INode[] =
      state.document.pages
        .find((block: IDocumentPage) => block.editorBlock?.contentId === contentId)
        ?.editorBlock.content.sections.find(({ id }: ISection) => id === sourceSectionId)
        ?.rows.find(({ id }: IRow) => id === sourceRowId)
        ?.cells.find(({ id }: ICell) => id === sourceCellId)
        ?.nodes.filter(({ id }: INode) => sourceNodeIds.includes(id)) || [];

    if (!nodes.length) {
      return;
    }

    state.document.pages = EditorHelper.deleteNodes(state, {
      contentId,
      targetSectionId: sourceSectionId,
      targetRowId: sourceRowId,
      targetCellId: sourceCellId,
      targetNodeIds: sourceNodeIds,
    });

    state.document.pages = EditorHelper.addNodes(state, {
      contentId,
      targetSectionId,
      targetRowId,
      targetCellId,
      targetNodeId,
      append,
      nodes,
    });
  },

  [EEditorContentMutation.DeleteNode](
    state: IDealroomState,
    { contentId, targetSectionId, targetRowId, targetCellId, targetNodeId }: IDeleteNodeRequest
  ) {
    state.document.pages = EditorHelper.deleteNodes(state, {
      contentId,
      targetSectionId,
      targetRowId,
      targetCellId,
      targetNodeIds: [targetNodeId],
    });
  },
};
